<template>
  <div
    class="group"
    :class="{
      'flex items-center justify-between': flex,
      'w-full': wFull,
    }"
  >
    <t-label
      v-if="label && !labelIn && !$slots.label"
      :for="uuid"
      :disabled="disabled"
      :changed="changed"
      :tooltip="resetTooltip"
      :reserve-reset="flex"
      @reset="$emit('reset')"
      >{{ label }}</t-label
    >
    <t-label
      v-if="$slots.label && !labelIn"
      v-tooltip="{
        content: label,
      }"
      :for="uuid"
      :disabled="disabled"
      :changed="changed"
      :tooltip="resetTooltip"
      :reserve-reset="flex"
      @reset="$emit('reset')"
    >
      <slot name="label"></slot>
    </t-label>
    <div
      ref="outsideNumberInput"
      class="inline-flex relative rounded-md transition duration-300 focus-within:outline focus-within:outline-2 focus-within:outline-accent-1"
      :class="{
        'w-full': wFull,
      }"
    >
      <div
        class="flex relative rounded-md border-x border-y border-solid border-transparent bg-gray-600 px-2.5 font-inter text-xs font-medium leading-[30px] text-gray-50 transition duration-300 group-focus-within:bg-gray-700 group-focus-within:text-gray-10 group-hover:border-gray-400"
        :class="{
          '!pl-2 !pr-1': controls,
          '!border-transparent !bg-gray-600 !text-gray-200': disabled,
          'w-full justify-between': wFull,
        }"
        @click="($refs.thisInput as HTMLInputElement).focus()"
      >
        <div v-if="label && labelIn" class="text-[9px] font-medium text-gray-200">
          <span>{{ label }}</span>
        </div>
        <input
          :id="uuid"
          ref="thisInput"
          :value="inputDisplayValue"
          class="border-none bg-gray-600 p-0 font-inter text-xs font-medium leading-[30px] text-gray-50 outline-none transition duration-300 disabled:!cursor-not-allowed disabled:bg-gray-600 disabled:text-gray-200 group-focus-within:bg-gray-700 group-focus-within:text-gray-10"
          :style="{
            width: width + 'px',
          }"
          :class="{
            '!w-full': wFull,
          }"
          :disabled="disabled"
          @wheel="onWheel"
          @input="onInputEvent"
          @blur="onBlurOrEnter"
          @keydown.down.prevent="onArrowKey(1, $event)"
          @keydown.up.prevent="onArrowKey(-1, $event)"
          @keydown.enter="onEnterKey"
          @keydown.escape="(closeSuggestions(), (showDrop = false))"
        />
        <div v-if="controls" class="my-[2px] mx-0.5">
          <button
            class="block cursor-pointer border-none bg-gray-600 py-[2.5px] text-gray-100 transition duration-300 hover:bg-gray-400 hover:text-gray-50 disabled:!cursor-not-allowed disabled:!border-transparent disabled:bg-gray-600 disabled:text-gray-300 group-hover:border-gray-400"
            :disabled="disabled"
            @click="(e) => (e.shiftKey ? add(5) : add(Number(step)))"
          >
            <svg
              width="12"
              height="8"
              viewBox="0 0 12 9"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.22052 2.72852L2.22052 5.72852C2.00958 5.93945 1.93927 6.26758 2.05645 6.54883C2.17364 6.83008 2.45489 7.01758 2.75958 7.01758H8.75958C9.06427 7.01758 9.32208 6.83008 9.43927 6.54883C9.55645 6.26758 9.48614 5.93945 9.2752 5.72852L6.2752 2.72852C5.99395 2.42383 5.50177 2.42383 5.22052 2.72852Z"
                fill="currentColor"
              />
            </svg>
          </button>
          <button
            class="block cursor-pointer border-none bg-gray-600 py-[2.5px] text-gray-100 transition duration-300 hover:bg-gray-400 hover:text-gray-50 disabled:!cursor-not-allowed disabled:!border-transparent disabled:bg-gray-600 disabled:text-gray-300 group-hover:border-gray-400"
            :disabled="disabled"
            @click="(e) => (e.shiftKey ? substract(5) : substract(Number(step)))"
          >
            <svg
              width="12"
              height="8"
              viewBox="0 0 12 9"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.22052 6.78906L2.22052 3.78906C2.00958 3.57812 1.93927 3.25 2.05645 2.96875C2.17364 2.6875 2.45489 2.5 2.75958 2.5H8.75958C9.06427 2.5 9.32208 2.6875 9.43927 2.96875C9.55645 3.25 9.48614 3.57812 9.2752 3.78906L6.2752 6.78906C5.99395 7.09375 5.50177 7.09375 5.22052 6.78906Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
        <button
          v-if="units.length > 0"
          class="block w-6 h-6 cursor-pointer"
          :class="{
            '!cursor-not-allowed': disabled,
          }"
          :disabled="disabled"
          @click="toggleUnitDrop()"
        >
          <span>{{ unit }}</span>
        </button>
        <ul
          v-if="showDrop && units.length > 0"
          ref="unitDrop"
          class="absolute right-0 top-[calc(100%+10px)] z-10 max-h-[200px] overflow-hidden bg-gray-600 rounded-md border-gray-400 border"
        >
          <PerfectScrollbar
            style="max-height: 200px"
            :options="{ wheelPropagation: false, suppressScrollX: true }"
          >
            <li
              v-for="u in units"
              :key="u"
              @click="setUnit(u)"
              class="cursor-pointer transition duration-300 hover:bg-gray-400 px-3"
              :class="{
                'bg-gray-400 text-gray-50': u === unit,
              }"
            >
              {{ u }}
            </li>
            <template v-if="allowedStrings.length > 0">
              <li class="border-t border-gray-400 my-0.5"></li>
              <li
                v-for="s in allowedStrings"
                :key="s"
                @click="selectSuggestion(s)"
                class="cursor-pointer transition duration-300 hover:bg-gray-400 px-3 text-gray-100"
                :class="{
                  'bg-gray-400 !text-gray-50': model === s,
                }"
              >
                {{ s }}
              </li>
            </template>
          </PerfectScrollbar>
        </ul>
        <ul
          v-if="showSuggestions && filteredSuggestions.length > 0"
          ref="suggestionsDrop"
          class="absolute left-0 top-[calc(100%+6px)] z-10 w-full list-none bg-gray-600 rounded-md overflow-hidden border-gray-400 border p-0"
        >
          <li
            v-for="(s, index) in filteredSuggestions"
            :key="s"
            class="cursor-pointer px-3 font-inter text-xs font-medium leading-6 text-gray-100 transition duration-300 hover:bg-gray-400 hover:text-gray-50"
            :class="{
              'bg-gray-400 text-gray-50': index === highlightedIndex,
            }"
            @mousedown.prevent
            @click="selectSuggestion(s)"
          >
            {{ s }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { v4 as uuidv4 } from "uuid";
import TLabel from "../TLabel/TLabel.vue";
import { PerfectScrollbar } from "vue3-perfect-scrollbar";
import "vue3-perfect-scrollbar/style.css";
import { onClickOutside } from "@vueuse/core";
import {
  ref,
  computed,
  watch,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  useTemplateRef,
} from "vue";

const props = withDefaults(
  defineProps<{
    controls?: boolean;
    min?: number;
    max?: number;
    disabled?: boolean;
    alt?: boolean;
    label?: string;
    noBorderLeft?: boolean;
    regexPattern?: string;
    step?: string;
    width?: number;
    flex?: boolean;
    labelIn?: boolean;
    wFull?: boolean;
    units?: string[];
    allowedStrings?: string[];
    changed?: boolean;
    resetTooltip?: string;
  }>(),
  {
    controls: true,
    noBorderLeft: true,
    step: "1",
    width: 55,
    units: () => ["px", "em", "rem", "%", "vh", "vw"],
    allowedStrings: () => [],
    resetTooltip: "Clear value",
  },
);

defineEmits(["reset"]);

const model = defineModel<string | number>();

const uuid = uuidv4();
const active = ref(false);
const outsideNumberInput = useTemplateRef("outsideNumberInput");
const unitDrop = useTemplateRef("unitDrop");
const keyIndex = ref(0);
const showDrop = ref(false);
const showSuggestions = ref(false);
const highlightedIndex = ref(-1);
const rawInputText = ref("");
const isEditing = ref(false);
let isInternalUpdate = false;

const inputDisplayValue = computed(() => {
  if (isEditing.value) return rawInputText.value;
  return valueWithoutUnit.value;
});

// Reset editing state when model changes externally (undo, reset, programmatic change)
watch(model, () => {
  if (isEditing.value && !isInternalUpdate) {
    isEditing.value = false;
    rawInputText.value = "";
  }
});

const filteredSuggestions = computed(() => {
  if (props.allowedStrings.length === 0) return [];
  if (!rawInputText.value || !isNaN(parseFloat(rawInputText.value))) return [];
  const search = rawInputText.value.toLowerCase().trim();
  return props.allowedStrings.filter((s) => s.toLowerCase().includes(search));
});

const valueWithoutUnit = computed({
  get() {
    if (!model.value) {
      return "";
    }
    if (typeof model.value === "number") {
      return model.value;
    }
    const value = parseFloat(model.value);
    if (isNaN(value)) {
      return model.value;
    }
    return value;
  },
  set(value: string) {
    isInternalUpdate = true;
    if (!isNaN(parseFloat(value))) {
      if (props.units.length === 0) {
        model.value = checkValue(parseFloat(value));
      } else {
        const newUnit = checkUnit(value);
        const unitToSet = newUnit ? newUnit : unit.value !== "-" ? unit.value : "px";
        model.value = checkValue(parseFloat(value)) + unitToSet;
      }
    } else if (props.allowedStrings.includes(value)) {
      model.value = value;
    }
    nextTick(() => {
      isInternalUpdate = false;
    });
  },
});

const unit = computed({
  get() {
    if (props.units.length === 0) {
      return "";
    }
    if (typeof model.value === "number") {
      return "px";
    }
    if (typeof valueWithoutUnit.value === "number" && model.value) {
      const unit = model.value.replace(valueWithoutUnit.value.toString(), "");
      return unit || "-";
    }
    return "-";
  },
  set(value) {
    isInternalUpdate = true;
    if (props.units.length === 0) {
      return;
    }
    if (typeof valueWithoutUnit.value === "number") {
      model.value = valueWithoutUnit.value + value;
    } else {
      model.value = 0 + value;
    }
    nextTick(() => {
      isInternalUpdate = false;
    });
  },
});

function checkUnit(value: string) {
  const unitString = value.replace(parseFloat(value).toString(), "");
  return props.units.find((item) => item == unitString);
}

function checkValue(value: number) {
  value = Number(value);
  let validatedNumber = value;

  if (props.min !== undefined) {
    if (value < props.min) {
      validatedNumber = props.min;
      keyIndex.value++;
    }
  }

  if (props.max !== undefined) {
    if (value > props.max) {
      validatedNumber = props.max;
      keyIndex.value++;
    }
  }

  return validatedNumber;
}

function onWheel(event: WheelEvent) {
  if (event.altKey) {
    event.preventDefault();
    event.stopPropagation();

    if (event.deltaY < 0) {
      add();
    } else {
      substract();
    }
  }
}

function onBlurOrEnter() {
  if (isEditing.value && rawInputText.value) {
    valueWithoutUnit.value = rawInputText.value;
  }
  isEditing.value = false;
  rawInputText.value = "";
  closeSuggestions();
}

function onInputEvent(e: Event) {
  const value = (e.target as HTMLInputElement).value;
  rawInputText.value = value;
  isEditing.value = true;
  highlightedIndex.value = -1;
  showDrop.value = false;

  // Commit numeric values immediately (preserves original v-model behavior)
  if (!isNaN(parseFloat(value))) {
    valueWithoutUnit.value = value;
    showSuggestions.value = false;

    // If the value was clamped by min/max, sync the displayed text back
    const committed = String(valueWithoutUnit.value ?? "");
    if (committed !== value) {
      rawInputText.value = committed;
      isEditing.value = false;
    }
    return;
  }

  // For non-numeric text, defer commit to blur and show suggestions
  if (value && props.allowedStrings.length > 0) {
    const search = value.toLowerCase().trim();
    showSuggestions.value = props.allowedStrings.some((s) => s.toLowerCase().includes(search));
  } else {
    showSuggestions.value = false;
  }
}

function selectSuggestion(s: string) {
  valueWithoutUnit.value = s;
  isEditing.value = false;
  rawInputText.value = "";
  closeSuggestions();
  showDrop.value = false;
}

function closeSuggestions() {
  showSuggestions.value = false;

  highlightedIndex.value = -1;
}

function onArrowKey(direction: number, e: KeyboardEvent) {
  if (showSuggestions.value && filteredSuggestions.value.length > 0) {
    highlightedIndex.value = Math.max(
      0,
      Math.min(filteredSuggestions.value.length - 1, highlightedIndex.value + direction),
    );
    return;
  }

  const step = e.shiftKey ? 5 : Number(props.step);
  if (direction === -1) {
    add(step);
  } else {
    substract(step);
  }
}

function onEnterKey(e: KeyboardEvent) {
  if (showSuggestions.value && highlightedIndex.value >= 0) {
    e.preventDefault();
    selectSuggestion(filteredSuggestions.value[highlightedIndex.value]);
  } else {
    onBlurOrEnter();
  }
}

function setUnit(u: string) {
  unit.value = u;
  showDrop.value = false;
}

onClickOutside(outsideNumberInput, () => {
  active.value = false;
  closeSuggestions();
});

onClickOutside(unitDrop, () => {
  showDrop.value = false;
});

function add(toAdd = 1) {
  if (!props.disabled) {
    active.value = true;
    isEditing.value = false;

    if (typeof valueWithoutUnit.value === "number") {
      const newValue = valueWithoutUnit.value + toAdd;

      if (props.max !== undefined && newValue > props.max) {
        valueWithoutUnit.value = (Math.round(props.max * 100) / 100).toString();
      } else {
        valueWithoutUnit.value = (Math.round(newValue * 100) / 100).toString();
      }
    } else {
      valueWithoutUnit.value = "0";
    }
  }
}

function substract(toSub = 1) {
  if (!props.disabled) {
    active.value = true;
    isEditing.value = false;

    if (typeof valueWithoutUnit.value === "number") {
      const newValue = valueWithoutUnit.value - toSub;

      if (props.min !== undefined && newValue < props.min) {
        valueWithoutUnit.value = (Math.round(props.min * 100) / 100).toString();
      } else {
        valueWithoutUnit.value = (Math.round(newValue * 100) / 100).toString();
      }
    } else {
      valueWithoutUnit.value = "0";
    }
  }
}

function focusChanged(event: Event) {
  const el = event.target;

  if (!el) {
    return;
  }

  //@ts-expect-error
  if (el.id === uuid) {
    active.value = true;
  } else {
    active.value = false;
  }
}

function toggleUnitDrop() {
  if (props.disabled) {
    return;
  }
  showDrop.value = !showDrop.value;
  closeSuggestions();
}

onBeforeMount(() => {
  document.addEventListener("focusin", focusChanged);
});

onBeforeUnmount(() => {
  document.removeEventListener("focusin", focusChanged);
});
</script>
<style lang="scss">
// @import "./TInputNumber.scss";
input[type="number"] {
  -moz-appearance: textfield;
  /* Firefox */
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  /* display: none; <- Crashes Chrome on hover */
  -webkit-appearance: none;
  margin: 0;
  /* <-- Apparently some margin are still there even though it's hidden */
}
</style>
